import React, { useState, useCallback } from 'react';

// Rotating knob component - responsive size
function Knob({ value, min, max, onChange, label, unit = '', color = '#F5C842', size = 56, mobileSize = 44 }) {
  // Use smaller size on mobile via CSS or window check
  const [actualSize, setActualSize] = React.useState(size);
  
  React.useEffect(() => {
    const checkSize = () => {
      setActualSize(window.innerWidth < 640 ? mobileSize : size);
    };
    checkSize();
    window.addEventListener('resize', checkSize);
    return () => window.removeEventListener('resize', checkSize);
  }, [size, mobileSize]);
  const [dragging, setDragging] = useState(false);
  const startY = React.useRef(0);
  const startVal = React.useRef(value);

  const norm = (value - min) / (max - min);
  const angle = -140 + norm * 280; // -140deg to +140deg

  const handleMouseDown = (e) => {
    setDragging(true);
    startY.current = e.clientY;
    startVal.current = value;
    e.preventDefault();
  };

  const handleMouseMove = useCallback((e) => {
    if (!dragging) return;
    const delta = (startY.current - e.clientY) / 150;
    const newVal = Math.max(min, Math.min(max, startVal.current + delta * (max - min)));
    onChange(Math.round(newVal * 10) / 10);
  }, [dragging, min, max, onChange]);

  const handleMouseUp = () => setDragging(false);

  React.useEffect(() => {
    if (dragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchmove', (e) => handleMouseMove(e.touches[0]), { passive: false });
      window.addEventListener('touchend', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragging, handleMouseMove]);

  const radius = actualSize / 2 - 6;
  const cx = actualSize / 2;
  const cy = actualSize / 2;
  const startAngle = (-140 - 90) * (Math.PI / 180);
  const endAngle = (angle - 90) * (Math.PI / 180);

  const arcPath = (start, end, r) => {
    const x1 = cx + r * Math.cos(start);
    const y1 = cy + r * Math.sin(start);
    const x2 = cx + r * Math.cos(end);
    const y2 = cy + r * Math.sin(end);
    const large = Math.abs(end - start) > Math.PI ? 1 : 0;
    return `M ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2}`;
  };

  return (
    <div className="flex flex-col items-center gap-1 select-none" data-testid={`knob-${label}`}>
      <svg
        width={actualSize} height={actualSize}
        className={`cursor-pointer ${dragging ? 'cursor-grabbing' : 'cursor-grab'}`}
        onMouseDown={handleMouseDown}
        onTouchStart={(e) => { setDragging(true); startY.current = e.touches[0].clientY; startVal.current = value; }}
      >
        <defs>
          <radialGradient id={`knob-grad-${label}`} cx="40%" cy="35%">
            <stop offset="0%" stopColor="#2A2A30" />
            <stop offset="60%" stopColor="#1A1A1F" />
            <stop offset="100%" stopColor="#0F0F12" />
          </radialGradient>
          <filter id={`glow-${label}`}>
            <feGaussianBlur stdDeviation="2" result="blur" />
            <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
        </defs>
        {/* Outer ring shadow */}
        <circle cx={cx} cy={cy} r={radius + 1} fill="none" stroke="#0A0A0A" strokeWidth={2} />
        {/* Track */}
        <path d={arcPath(startAngle, (140 - 90) * (Math.PI / 180), radius)} fill="none" stroke="#1A1A1F" strokeWidth={3.5} strokeLinecap="round" />
        {/* Value arc */}
        {norm > 0 && <path d={arcPath(startAngle, endAngle, radius)} fill="none" stroke={color} strokeWidth={3.5} strokeLinecap="round"
          filter={dragging ? `url(#glow-${label})` : undefined} opacity={dragging ? 1 : 0.85} />}
        {/* Indicator dot */}
        <circle
          cx={cx + (radius - 2) * Math.cos((angle - 90) * (Math.PI / 180))}
          cy={cy + (radius - 2) * Math.sin((angle - 90) * (Math.PI / 180))}
          r={dragging ? 4 : 3} fill={color}
          style={{ transition: 'r 0.15s ease', filter: dragging ? `drop-shadow(0 0 4px ${color}80)` : 'none' }}
        />
        {/* Metallic center */}
        <circle cx={cx} cy={cy} r={radius - 7} fill={`url(#knob-grad-${label})`} stroke="#27272A" strokeWidth={0.5} />
        {/* Notch line */}
        <line
          x1={cx} y1={cy - radius + 14}
          x2={cx + (radius - 18) * Math.sin(angle * Math.PI / 180)}
          y2={cy - (radius - 18) * Math.cos(angle * Math.PI / 180)}
          stroke={color} strokeWidth={1.5} strokeLinecap="round" opacity={0.7}
        />
        <text x={cx} y={cy + 4} textAnchor="middle" fontSize={10} fill="white" fontWeight="bold" style={{ userSelect: 'none' }}>
          {value}{unit}
        </text>
      </svg>
      <span className="text-[9px] text-[#71717A] font-medium uppercase tracking-wide">{label}</span>
    </div>
  );
}

// BPM tap tempo
function BPMControl({ bpm, onBpmChange }) {
  const [taps, setTaps] = useState([]);

  const tap = () => {
    const now = Date.now();
    const recent = [...taps, now].filter(t => now - t < 3000).slice(-8);
    setTaps(recent);
    if (recent.length >= 2) {
      const intervals = recent.slice(1).map((t, i) => t - recent[i]);
      const avgInterval = intervals.reduce((a, b) => a + b, 0) / intervals.length;
      const newBpm = Math.round(60000 / avgInterval);
      if (newBpm >= 40 && newBpm <= 220) onBpmChange(newBpm);
    }
  };

  return (
    <div className="flex flex-col items-center gap-1 sm:gap-2">
      <div className="text-2xl sm:text-3xl font-bold text-white tabular-nums" data-testid="bpm-display">{bpm}</div>
      <div className="text-[8px] sm:text-[9px] text-[#A1A1AA] uppercase tracking-wider">BPM</div>
      <div className="flex gap-0.5 sm:gap-1">
        <button onClick={() => onBpmChange(Math.max(40, bpm - 1))} className="w-5 sm:w-6 h-5 sm:h-6 rounded bg-[#27272A] text-[#A1A1AA] hover:text-white text-[10px] sm:text-xs transition-colors">−</button>
        <button
          data-testid="tap-tempo-btn"
          onClick={tap}
          className="relative z-10 px-2 sm:px-3 py-0.5 sm:py-1 rounded text-[9px] sm:text-[10px] font-bold text-black transition-all active:scale-95"
          style={{ background: 'linear-gradient(135deg, #F5C842, #FF6B00)' }}
        >
          TAP
        </button>
        <button onClick={() => onBpmChange(Math.min(220, bpm + 1))} className="w-5 sm:w-6 h-5 sm:h-6 rounded bg-[#27272A] text-[#A1A1AA] hover:text-white text-[10px] sm:text-xs transition-colors">+</button>
      </div>
    </div>
  );
}

// Musical key display
const KEYS = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
const MODES = ['maj', 'min'];

function KeyControl({ keyVal, onKeyChange }) {
  const [root, octave] = [keyVal?.replace(/m$|maj$|min$/, '') || 'A', keyVal?.includes('m') ? 'min' : 'maj'];
  const rootIdx = KEYS.indexOf(root) >= 0 ? KEYS.indexOf(root) : 9;
  const [mode, setMode] = useState(octave);

  const transposeUp = () => { const next = KEYS[(rootIdx + 1) % 12]; onKeyChange(next + (mode === 'min' ? 'm' : '')); };
  const transposeDown = () => { const prev = KEYS[(rootIdx + 11) % 12]; onKeyChange(prev + (mode === 'min' ? 'm' : '')); };
  const toggleMode = () => { const nm = mode === 'min' ? 'maj' : 'min'; setMode(nm); onKeyChange(root + (nm === 'min' ? 'm' : '')); };

  return (
    <div className="flex flex-col items-center gap-1 sm:gap-2">
      <div className="flex items-center gap-0.5 sm:gap-1">
        <button onClick={transposeDown} className="w-5 sm:w-6 h-5 sm:h-6 rounded bg-[#27272A] text-[#A1A1AA] hover:text-white text-[10px] sm:text-xs">♭</button>
        <div className="text-xl sm:text-2xl font-bold text-white w-10 sm:w-12 text-center" data-testid="key-display">{root}</div>
        <button onClick={transposeUp} className="w-5 sm:w-6 h-5 sm:h-6 rounded bg-[#27272A] text-[#A1A1AA] hover:text-white text-[10px] sm:text-xs">♯</button>
      </div>
      <button
        onClick={toggleMode}
        className="text-[9px] sm:text-[10px] px-1.5 sm:px-2 py-0.5 rounded border transition-colors"
        style={{ borderColor: mode === 'min' ? '#7000FF' : '#F5C842', color: mode === 'min' ? '#7000FF' : '#F5C842' }}
        data-testid="key-mode-btn"
      >
        {mode === 'min' ? 'Minor' : 'Major'}
      </button>
    </div>
  );
}

// Effect toggle button
function EffectBtn({ label, active, color, onToggle, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onToggle}
      className="flex flex-col items-center gap-1 p-2 rounded-xl border transition-all"
      style={{
        borderColor: active ? color : '#27272A',
        background: active ? `${color}20` : 'transparent',
      }}
    >
      <div className="w-2 h-2 rounded-full transition-colors" style={{ background: active ? color : '#27272A' }} />
      <span className="text-[10px] font-medium" style={{ color: active ? color : '#71717A' }}>{label}</span>
    </button>
  );
}

export default function ProducerDJPanel({
  bpm = 120,
  musicalKey = 'Am',
  energy = 7,
  onBpmChange,
  onKeyChange,
  onEQChange,
  onEffectChange,
  disabled = false,
  className = '',
}) {
  const [bass, setBass] = useState(6);
  const [mid, setMid] = useState(5);
  const [treble, setTreble] = useState(6);
  const [reverb, setReverb] = useState(3);
  const [delay, setDelay] = useState(2);
  const [filter, setFilter] = useState(10);
  const [effects, setEffects] = useState({ loop: false, reverb: false, delay: false, filter: false, sidechain: false });

  const handleEQ = (band, val, setter) => {
    setter(val);
    onEQChange?.(band, val);
  };

  const toggleEffect = (name) => {
    const updated = { ...effects, [name]: !effects[name] };
    setEffects(updated);
    onEffectChange?.(name, updated[name]);
  };

  return (
    <div className={`flex flex-col gap-2 sm:gap-4 ${disabled ? 'opacity-50 pointer-events-none' : ''} ${className}`} data-testid="dj-panel">
      {/* BPM + Key row - stack on very small screens */}
      <div className="flex flex-col xs:flex-row items-center justify-around gap-2 xs:gap-0 bg-[#0A0A0B] rounded-xl p-2 sm:p-3 border border-[#27272A]">
        <BPMControl bpm={bpm} onBpmChange={onBpmChange || (() => {})} />
        <div className="hidden xs:block w-px h-10 sm:h-12 bg-[#27272A]" />
        <div className="xs:hidden w-full h-px bg-[#27272A]" />
        <KeyControl keyVal={musicalKey} onKeyChange={onKeyChange || (() => {})} />
      </div>

      {/* EQ Knobs */}
      <div className="bg-[#0A0A0B] rounded-xl p-2 sm:p-3 border border-[#27272A]">
        <p className="text-[8px] sm:text-[9px] text-[#A1A1AA] font-medium uppercase tracking-wider mb-2 sm:mb-3 text-center">EQ</p>
        <div className="flex justify-around gap-1">
          <Knob value={bass}   min={0} max={10} onChange={v => handleEQ('bass', v, setBass)}   label="Bass"   color="#7000FF" size={56} mobileSize={42} />
          <Knob value={mid}    min={0} max={10} onChange={v => handleEQ('mid', v, setMid)}     label="Mid"    color="#F5C842" size={56} mobileSize={42} />
          <Knob value={treble} min={0} max={10} onChange={v => handleEQ('treble', v, setTreble)} label="Treble" color="#00D4FF" size={56} mobileSize={42} />
        </div>
      </div>

      {/* FX Knobs */}
      <div className="bg-[#0A0A0B] rounded-xl p-2 sm:p-3 border border-[#27272A]">
        <p className="text-[8px] sm:text-[9px] text-[#A1A1AA] font-medium uppercase tracking-wider mb-2 sm:mb-3 text-center">FX</p>
        <div className="flex justify-around gap-1">
          <Knob value={reverb} min={0} max={10} onChange={v => { setReverb(v); onEQChange?.('reverb', v); }} label="Reverb" color="#FF6B00" size={56} mobileSize={42} />
          <Knob value={delay}  min={0} max={10} onChange={v => { setDelay(v); onEQChange?.('delay', v); }}  label="Delay"  color="#FF0066" size={56} mobileSize={42} />
          <Knob value={filter} min={0} max={20} onChange={v => { setFilter(v); onEQChange?.('filter', v); }} label="Filter" unit="k" color="#00FF88" size={56} mobileSize={42} />
        </div>
      </div>

      {/* Effect toggles - responsive grid */}
      <div className="grid grid-cols-5 gap-0.5 sm:gap-1">
        <EffectBtn label="Loop"     active={effects.loop}     color="#00D4FF" onToggle={() => toggleEffect('loop')}     testId="fx-loop" />
        <EffectBtn label="Rev"      active={effects.reverb}   color="#FF6B00" onToggle={() => toggleEffect('reverb')}   testId="fx-reverb" />
        <EffectBtn label="Delay"    active={effects.delay}    color="#FF0066" onToggle={() => toggleEffect('delay')}    testId="fx-delay" />
        <EffectBtn label="Filter"   active={effects.filter}   color="#00FF88" onToggle={() => toggleEffect('filter')}   testId="fx-filter" />
        <EffectBtn label="Side"     active={effects.sidechain} color="#7000FF" onToggle={() => toggleEffect('sidechain')} testId="fx-sidechain" />
      </div>
    </div>
  );
}
